'use client';

import cx from 'classix';
import parse from 'html-react-parser';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BsGeoAltFill } from 'react-icons/bs';
import { useInView } from 'react-intersection-observer';

import ListingCard from '@/features/listing/components/listing-card/listing-card';
import ListingMessageButton from '@/features/listing/components/listing-card/listing-message/listing-message-button';
import ListingMoveInDate from '@/features/listing/components/listing-card/listing-move-in-date/listing-move-in-date';
import ListingRate from '@/features/listing/components/listing-card/listing-rate/listing-rate';
import ListingUser from '@/features/listing/components/listing-card/listing-user/listing-user';
import ListingVerifications
  from '@/features/listing/components/listing-card/listing-verifications/listing-verifications';
import ListingDetailGallery
  from '@/features/listing/components/listing-detailed/listing-detail-gallery/listing-detail-gallery';
import ListingDetailMap from '@/features/listing/components/listing-detailed/listing-detail-map/listing-detail-map';
import ListingDetailRoomInfo
  from '@/features/listing/components/listing-detailed/listing-detail-room-info/listing-detail-room-info';
import ListingDetailActions
  from '@/features/listing/components/listing-detailed/listings-detail-actions/listing-detail-actions';
import ListingMessage from '@/features/listing/components/listing-message/listing-message';
import ListingReportModal from '@/features/listing/components/listing-report-modal/listing-report-modal';
import { DEFAULT_LISTING_PART_VARIANTS } from '@/features/listing/constants/listing-constants';
import {
  type ListingType,
  ListingTypeId,
  type ListingWithUser,
} from '@/features/listing/types/listing-types';
import { type UserListingsData } from '@/features/listing/types/user-listings-types';
import placeNameToUrl from '@/features/place/utils/place-name-to-url';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { Link } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Button from '@/ui/button/button';

import styles from './listing-detailed.module.scss';

interface ListingDetailedComponentProps {
  listing: ListingWithUser;
  userListings?: UserListingsData | null;
}

export default function ListingDetailed(props: ListingDetailedComponentProps) {
  const {
    listing,
    userListings,
  } = props;

  const updateUrl = () => {
    const windowPathname = window.location.pathname;
    const windowCurrentId = windowPathname.split('/listings/')[1];

    if (windowCurrentId !== String(listing.id)) {
      const updatedPathName = windowPathname.replace(String(windowCurrentId), String(listing.id));
      window.history.pushState({}, '', updatedPathName);
    }
  };

  const [markerRef] = useInView({
    onChange: (inView) => {
      if (inView) {
        updateUrl();
      }
    },
    threshold: 0.3,
  });

  // const markerRef = useRef<HTMLDivElement>(null);

  const listingType = ListingTypeId[listing.listingTypeId] as ListingType;
  const isListingPerson = listingType === 'needapartment' || listingType === 'needroom';
  const listingUrlPart = DEFAULT_LISTING_PART_VARIANTS[listingType];

  const listingDetailTranslations = useTranslations('listing.detail');

  let otherUserListings: UserListingsData['listings'] = [];

  if (userListings) {
    otherUserListings = userListings.listings.filter((userListing) => userListing.id !== listing.id);
  }

  const [isReportModalShow,
    setIsReportModalShow] = useState<boolean>(false);

  const handleReportModalClose = () => {
    setIsReportModalShow(false);
  };

  const handleShowReportModal = () => {
    setIsReportModalShow(true);
  };

  const profileAppFlyerLink = useAppFlyerLink('mobile', 'mobileListingUserAvatar', `/profile/${String(listing.user.id)}/listings`);

  return (
    <>
      <section ref={markerRef} className={styles.listingDetailed}>
        <div className={styles.listingDetailed_body}>
          <div className={styles.listingDetailed_gallery}>
            <ListingDetailGallery
              images={listing.images}
              alt={listing.title}
              listingId={listing.id}
              isBookmarked={listing.isBookmarked}
              isNew={listing.isNew}
              listingRefreshedDate={listing.refreshed}
              listingType={listingType}
            />
          </div>

          <h1 className={styles.listingDetailed_title}>
            {listing.title}
          </h1>

          {/* <div style={{ height: '1px' }} ref={markerRef} /> */}

          <div className={styles.listingDetailed_content}>
            <div className={cx(styles.listingDetailed_info, styles.listingDetailed_box)}>
              <div className={styles.listingDetailed_infoItem}>
                <p className={styles.listingDetailed_infoTitle}>
                  {listingDetailTranslations('info.price-month')}
                </p>
                <div>
                  <ListingRate rate={listing.rates} view="isDetailed" />
                </div>
              </div>

              <div className={styles.listingDetailed_infoItem}>
                <p className={styles.listingDetailed_infoTitle}>
                  {listingDetailTranslations('info.listing-type')}
                </p>
                <div>
                  {listingDetailTranslations(`info.listing-type-value.${listingType}`)}
                </div>
              </div>

              <div className={styles.listingDetailed_infoItem}>
                <ListingMoveInDate
                  moveInDate={listing.calendar.dateIn}
                  view="isDetailed"
                  isListingPerson={isListingPerson}
                />
              </div>
            </div>

            <div className={styles.listingDetailed_box}>
              <ListingVerifications
                isAddressVerified={listing.addressVerificationStatus}
                userVerifications={listing.user.verifications}
              />
            </div>

            {!isListingPerson && (
              <div className={styles.listingDetailed_box}>
                <Link
                  className={styles.listingDetailed_geoAddress}
                  href={`/${listingUrlPart}/${placeNameToUrl(listing.addressFormatted)}`}
                >
                  <BsGeoAltFill className={styles.listingDetailed_addressIcon} />
                  <p>{listing.addressFormatted}</p>
                </Link>

                <ListingDetailMap coordinates={{
                  lat: listing.geoLocation.latitude,
                  lng: listing.geoLocation.longitude,
                }}
                />
              </div>
            )}

            <div className={styles.listingDetailed_box}>
              <p className={styles.listingDetailed_descriptionTitle}>
                {listingDetailTranslations('description')}
              </p>
              <p className={styles.listingDetailed_descriptionText}>
                {parse(listing.description.replaceAll(/\r\n|\r|\n/g, '<br />'))}
              </p>
            </div>

            {(listing.detailsRoom ?? listing.detailsApartment) && (
              <ListingDetailRoomInfo detailsRoom={listing.detailsRoom} detailsApartment={listing.detailsApartment} />
            )}
          </div>

          <div className={styles.listingDetailed_sidebar}>
            <div className={cx(styles.listingDetailed_user, styles.listingDetailed_box)}>
              <Link
                prefetch={false}
                // target="_blank"
                href={`${ROUTER_LINKS.profile}/${String(listing.user.id)}`}
              >
                <ListingUser user={listing.user} />
              </Link>
              <ListingMessage listing={listing} view="isDetailed" messageSrc="listingprofile" />
            </div>

            <ListingDetailActions
              listingId={listing.id}
              isBookmarked={listing.isBookmarked}
              listingType={listingType}
              onReportClick={handleShowReportModal}
            />
          </div>
        </div>

        {(otherUserListings.length > 0) && userListings && (
          <div className={cx(styles.listingDetailed_listings, styles.listingDetailed_box)}>
            <div className={styles.listingDetailed_listingsTitle}>
              <h3>
                {listingDetailTranslations('other-listings-from')}
                &nbsp;
                {listing.user.firstName}
              </h3>

              {otherUserListings.length > 2 && (
                <div className={styles.listingDetailed_listingsButton}>
                  <Button
                    href={`/profile/${String(listing.user.id)}/listings`}
                    element="link"
                    size="lg"
                    fontSize="s"
                    fullWidth
                  >
                    {listingDetailTranslations('user-listings-count', { count: userListings.count })}
                  </Button>
                </div>
              )}
            </div>

            <div className={styles.listingDetailed_listingsList}>
              {otherUserListings.map((userListing, index) => {
                const listingWithUser: ListingWithUser = {
                  ...userListing,
                  user: listing.user,
                };

                return (
                  <div
                    key={userListing.id}
                    className={cx(
                      styles.listingDetailed_listingCard,
                      index > 1 && styles.listingDetailed_listingCard__withFade,
                    )}
                  >
                    <ListingCard
                      listing={listingWithUser}
                      showMessageButton={false}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isReportModalShow && (
          <ListingReportModal
            listingId={listing.id}
            userId={listing.user.id}
            isActive={isReportModalShow}
            onClose={handleReportModalClose}
          />
        )}
      </section>

      <div className={styles.listingDetailed_mobileBar}>
        <Link
          prefetch={false}
          target="_blank"
          href={profileAppFlyerLink}
          style={{ lineHeight: '0' }}
        >
          <ListingUser user={listing.user} view="isDetailed" />
        </Link>

        <ListingDetailActions
          listingId={listing.id}
          isBookmarked={listing.isBookmarked}
          listingType={listingType}
          onReportClick={handleShowReportModal}
        />

        <div className={styles.listingDetailed_messageButton}>
          <ListingMessageButton listing={listing} view="isDetailed" messageSrc="listingprofile" />
        </div>
      </div>
    </>
  );
}
